import { useState, type ReactElement } from 'react';
import { ApiRoutes } from '@gr/core/api-routes';
import type { ApiKeyDto, IssuedApiKeyDto } from '@gr/core/accounts/identity/api-keys';
import { toolkitLocale } from '@gr/core/platform/toolkit/locale';
import type { OperationResult } from '@gr/utils/operation-result';
import { useServices, useT } from '../../../app/di';
import { useAction, useResource } from '../../../shared/hooks';
import { Alert } from '../../../ui-kit/Alert/Alert';
import { Button } from '../../../ui-kit/Button/Button';
import type { LoadOptions } from '../../../ui-kit/Combobox/Combobox';
import { useConfirm } from '../../../ui-kit/ConfirmDialog/ConfirmDialog';
import { Dialog } from '../../../ui-kit/Dialog/Dialog';
import { Loading } from '../../../ui-kit/Loading/Loading';
import { ApiKeyForm } from './ApiKeyForm/ApiKeyForm';
import { ApiKeysHelp } from './ApiKeysHelp/ApiKeysHelp';
import { ApiKeyTable } from './ApiKeyTable/ApiKeyTable';
import { IssuedApiKey } from './IssuedApiKey/IssuedApiKey';
import type { ApiKeyFormValues } from './model/api-keys';

export interface ApiKeysConnectedProps {
  showOwner?: boolean;
  loadOwners?: LoadOptions;
  list(): Promise<OperationResult<ApiKeyDto[]>>;
  issue(values: ApiKeyFormValues): Promise<OperationResult<IssuedApiKeyDto>>;
  revoke(keyId: string): Promise<OperationResult<ApiKeyDto>>;
  remove(keyId: string): Promise<OperationResult<null>>;
}

/**
 * Список, выпуск с показом ключа один раз (IAM-RULE-4), отзыв и удаление отозванного
 * (IAM-UC-13) с подтверждением. В демо — без выпуска, отзыва и удаления, зато с общим тестовым
 * ключом (DEMO-RULE-2).
 */
export function ApiKeysConnected({ showOwner = false, loadOwners, list, issue, revoke, remove }: ApiKeysConnectedProps): ReactElement {
  const { api } = useServices();
  const { t, te, locale } = useT();
  const confirm = useConfirm();
  const info = useResource(() => api.operations.info(), [api]);
  const keys = useResource(list, []);
  const revoking = useAction(revoke);
  const deleting = useAction(remove);
  const [dialog, setDialog] = useState<{ secret: string | null } | null>(null);
  const demo = info.data?.demo ?? false;
  const demoKey = info.data?.demoApiKey ?? null;
  const docsLang = `?lang=${toolkitLocale(locale)}`;

  const onRevoke = async (key: ApiKeyDto): Promise<void> => {
    if (!(await confirm({ title: t('identity.apiKeys.revokeConfirm', { name: key.name }), text: t('identity.apiKeys.revokeText'), confirmLabel: t('identity.apiKeys.revoke'), danger: true }))) return;
    if ((await revoking.run(key.id)).success) await keys.reload();
  };

  const onDelete = async (key: ApiKeyDto): Promise<void> => {
    if (!(await confirm({ title: t('identity.apiKeys.deleteConfirm', { name: key.name }), text: t('identity.apiKeys.deleteText'), confirmLabel: t('common.delete'), danger: true }))) return;
    if ((await deleting.run(key.id)).success) await keys.reload();
  };

  return (
    <div className="u-stack">
      {demo ? (
        <div className="u-stack">
          <Alert tone="info">{t('identity.apiKeys.demoText', { login: demoKey?.login ?? '' })}</Alert>
          {demoKey ? <IssuedApiKey secret={demoKey.secret} notice={t('identity.apiKeys.demoKeyNotice')} /> : null}
        </div>
      ) : info.data ? (
        <div className="u-row">
          <Button variant="primary" icon="key" onClick={() => setDialog({ secret: null })}>
            {t('identity.apiKeys.issue')}
          </Button>
        </div>
      ) : null}
      {revoking.error ? <Alert tone="danger">{te(revoking.error)}</Alert> : null}
      {deleting.error ? <Alert tone="danger">{te(deleting.error)}</Alert> : null}
      {keys.loading && !keys.data ? (
        <Loading label={t('common.loading')} />
      ) : keys.error ? (
        <Alert tone="danger" title={t('common.loadFailed')}>
          <Button size="sm" onClick={() => void keys.reload()}>
            {t('common.retry')}
          </Button>
        </Alert>
      ) : (
        <ApiKeyTable
          keys={keys.data ?? []}
          showOwner={showOwner}
          pending={revoking.pending || deleting.pending}
          onRevoke={demo ? undefined : (key) => void onRevoke(key)}
          onDelete={demo ? undefined : (key) => void onDelete(key)}
        />
      )}
      <ApiKeysHelp
        serverUrl={window.location.origin}
        guideUrl={`${ApiRoutes.toolkit.guide}${docsLang}`}
        skillUrl={`${ApiRoutes.toolkit.skill}${docsLang}`}
        skillZipUrl={`${ApiRoutes.toolkit.skillZip}${docsLang}`}
      />
      {dialog ? (
        <Dialog
          open
          title={dialog.secret ? t('identity.apiKeys.issuedTitle') : t('identity.apiKeys.issueTitle')}
          closeLabel={t('common.close')}
          onClose={() => setDialog(null)}
          footer={
            dialog.secret ? (
              <Button variant="primary" onClick={() => setDialog(null)}>
                {t('common.close')}
              </Button>
            ) : (
              <>
                <Button onClick={() => setDialog(null)}>{t('common.cancel')}</Button>
                <Button variant="primary" type="submit" form="issue-api-key">
                  {t('identity.apiKeys.issue')}
                </Button>
              </>
            )
          }
        >
          {dialog.secret ? (
            <IssuedApiKey secret={dialog.secret} />
          ) : (
            <ApiKeyForm
              id="issue-api-key"
              loadOwners={loadOwners}
              onSubmit={async (values) => {
                const result = await issue(values);
                if (result.success) {
                  setDialog({ secret: result.data.secret });
                  await keys.reload();
                }
                return result;
              }}
            />
          )}
        </Dialog>
      ) : null}
    </div>
  );
}
